import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import ScreeningTracker from "../components/ScreeningTracker";
import { KpiGrid } from "../components/ScreeningStatsWidgets";
import DateRangeFilter from "../components/DateRangeFilter";
import { addDays, todayInAppTimeZone } from "../utils/dateTime";
import { getScreeningStats, apiErrorMessage } from "../api/screeningApi";
import "../css/ClientList.css";
import "../css/ScreeningTracker.css";

// Last 30 days including today, as calendar days in the business time zone
// (utils/dateTime.js) — not the recruiter's own computer time zone.
function last30Days() {
  const today = todayInAppTimeZone();
  return { startDate: addDays(today, -29), endDate: today };
}

// "AI Screenings" page for TEAM_LEAD and RECRUITER.
// Shows ONLY the screenings this user scheduled — the backend enforces that
// from the login cookie, this page just displays what comes back.
// Admins are sent to the full admin dashboard instead.
export default function ScreeningList() {
  const { user } = useSelector((state) => state.auth);
  const isAdmin = user?.role === "ADMIN";

  const [range, setRange] = useState(last30Days);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState("");

  useEffect(() => {
    if (isAdmin) return;
    let cancelled = false;
    setStatsLoading(true);
    setStatsError("");
    // Empty dates are left out, so the server uses its default (last 30 days).
    const params = {};
    if (range.startDate) params.startDate = range.startDate;
    if (range.endDate) params.endDate = range.endDate;
    getScreeningStats(params)
      .then((res) => !cancelled && setStats(res.data))
      .catch((err) => !cancelled && setStatsError(apiErrorMessage(err, "Couldn't load your screening summary.")))
      .finally(() => !cancelled && setStatsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [range, isAdmin]);

  if (isAdmin) return <Navigate to="/admin/screenings" replace />;

  return (
    <div className="cl-page st-page">
      <header className="cl-header">
        <div>
          <h1>AI Screenings</h1>
          <p>Every screening you've scheduled — who opened the link, who finished, and how they scored.</p>
        </div>
      </header>

      <section className="st-panel">
        <div className="st-panel-head">
          <h2>Your summary</h2>
          <DateRangeFilter value={range} onChange={setRange} label="Scheduled" />
        </div>
        {statsError ? (
          <div className="cl-error" role="alert">
            {statsError}
          </div>
        ) : (
          <KpiGrid kpis={stats?.kpis} verdicts={stats?.verdicts} loading={statsLoading} />
        )}
      </section>

      <ScreeningTracker />
    </div>
  );
}
